<template>
  <div class="upload-table-buttons">
    <!-- Editing upload -->
    <div
      v-if="
        isUserRole([
          Role.Admin,
          Role.DocuResponsible,
          Role.DigitalizationCoworker,
        ])
      "
      v-tooltip.top="
        upload.canUpdate
          ? t('digitalizationUploadsOverview.tooltips.updateUpload')
          : t('digitalizationUploadsOverview.tooltips.uploadCannotBeUpdated')
      "
    >
      <FontAwesomeIcon
        :class="{
          'disabled-icon': !upload.canUpdate,
          'update-upload-icon': true,
        }"
        :icon="faPen"
        @click.stop="goToUploadUpdateView()"
      />
    </div>
    <!-- Soft deleting upload -->
    <template v-if="isUserRole([Role.Admin, Role.DocuResponsible])">
      <div
        v-if="!upload.isSoftDeleted"
        v-tooltip.top="
          t('digitalizationUploadsOverview.tooltips.softDeleteUpload')
        "
        @click.stop="setUploadSoftDeletion()"
      >
        <font-awesome-icon
          class="soft-delete-upload-icon"
          icon="fa-solid fa-bug"
        />
      </div>
      <div
        v-else
        v-tooltip.top="
          t('digitalizationUploadsOverview.tooltips.restoreSoftDeletedUpload')
        "
        @click.stop="setUploadSoftDeletion()"
      >
        <font-awesome-icon
          class="revert-soft-delete-upload-icon"
          icon="fa-solid fa-bug-slash"
        />
      </div>
    </template>
    <!-- Removing upload -->
    <div
      v-if="isUserRole([Role.Admin])"
      v-tooltip.top="
        upload.isRemovable
          ? t('digitalizationUploadsOverview.tooltips.removeUpload')
          : t('digitalizationUploadsOverview.tooltips.uploadCannotBeRemoved')
      "
    >
      <font-awesome-icon
        :class="{
          'remove-upload-icon': true,
          'disabled-icon': !upload.isRemovable,
        }"
        :icon="['fas', 'trash']"
        @click.stop="removeUpload()"
      />
    </div>
    <!-- Downloading upload confirmation -->
    <a
      v-tooltip.top="
        upload.isUploadConfirmationReady
          ? t(
              'digitalizationUploadsOverview.tooltips.downloadUploadConfirmation',
            )
          : t(
              'digitalizationUploadsOverview.tooltips.uploadConfirmationNotReady',
            )
      "
      :href="uploadConfirmationDownloadLink"
      target="_blank"
      @click.stop="!upload.isUploadConfirmationReady && $event.preventDefault()"
    >
      <font-awesome-icon
        :class="{
          'disabled-icon': !upload.isUploadConfirmationReady,
          'upload-confirmation-icon': true,
        }"
        :icon="['far', 'rectangle-list']"
      />
    </a>
  </div>
</template>

<script setup lang="ts">
  import { useRoles } from '@/composables/useRoles';
  import { useToast } from '@/composables/useToast';
  import { endpointsList } from '@/helpers';
  import { Route, routeParams } from '@/routes';
  import { faPen } from '@fortawesome/free-solid-svg-icons';
  import axios from 'axios';
  import {
    IDigitalizationUploadSummaryDto,
    ProjectType,
    Role,
    Serialized,
  } from 'types';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  const { t } = useI18n();
  const { isUserRole } = useRoles();
  const { createSuccessToast, createErrorToast } = useToast();
  const router = useRouter();

  const props = defineProps<{
    projectId: string;
    upload: Serialized<IDigitalizationUploadSummaryDto>;
  }>();

  const emit = defineEmits<{
    (e: 'loading'): void;
    (e: 'refetch'): void;
  }>();

  const uploadConfirmationDownloadLink = computed<string>(() => {
    const fileDownloadEndpoint =
      endpointsList.uploadHandler.downloadUploadConfirmationFile(
        ProjectType.DIGITALIZATION_PROJECT,
        props.upload._id,
      );
    return `${window.location.origin}/api/${fileDownloadEndpoint}`;
  });

  function goToUploadUpdateView(): void {
    router.push({
      name: Route.UPLOAD_UPDATE,
      params: {
        [routeParams[Route.UPLOAD_UPDATE].uploadId]: props.upload._id,
        [routeParams[Route.UPLOAD_UPDATE].projectId]: props.projectId,
      },
    });
  }

  async function removeUpload(): Promise<void> {
    emit('loading');
    try {
      await axios.delete(
        endpointsList.digitalizationUploads.removeUpload(props.upload._id),
      );
      createSuccessToast(t('digitalizationUploadsOverview.uploadRemoved'));
    } catch {
      createErrorToast(
        t('digitalizationUploadsOverview.errors.errorRemovingUpload'),
      );
    } finally {
      emit('refetch');
    }
  }

  async function setUploadSoftDeletion(): Promise<void> {
    const { isSoftDeleted } = props.upload;
    emit('loading');
    try {
      await axios.patch(
        endpointsList.digitalizationUploads.softDeleteUpload(
          props.upload._id,
          !isSoftDeleted,
        ),
      );
      createSuccessToast(
        isSoftDeleted
          ? t('digitalizationUploadsOverview.uploadRestored')
          : t('digitalizationUploadsOverview.uploadSoftDeleted'),
      );
    } catch {
      createErrorToast(
        isSoftDeleted
          ? t('digitalizationUploadsOverview.errors.errorRestoringUpload')
          : t('digitalizationUploadsOverview.errors.errorSoftDeletingUpload'),
      );
    } finally {
      emit('refetch');
    }
  }
</script>

<style lang="scss" scoped>
  .upload-table-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 1rem;

    @include disabled-icon;

    .update-upload-icon {
      color: $primary-color;
    }
    .soft-delete-upload-icon {
      color: $warning-color;
    }
    .revert-soft-delete-upload-icon {
      color: $success-color;
    }
    .upload-confirmation-icon {
      color: $primary-color;
    }
    .remove-upload-icon {
      color: $danger-color;
    }
  }
</style>
